import './TicketShowDetail.scss';

import { TextField } from '@refinedev/antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import FormattedTextInput from '@/components/general/FormattedTextInput';
import LinkTextField from '@/components/general/LinkTextField';
import PriorityLabel from '@/components/general/PriorityLabel';
import { ShowInfoGrid, ShowInfoItem } from '@/components/general/ShowInfo/ShowInfo';
import UploadedFileCarousel from '@/components/general/UploadFile/UploadedFileCarousel';
import { COMPANY_URLS, MACHINES_URLS } from '@/constants/urls';
import FileData from '@/model/General/FileData';
import { Ticket } from '@/model/Ticket/Ticket';
import { TicketContactTypeString } from '@/model/Ticket/TicketConstants';
import { ensureHtml } from '@/utils/ensureHtml';

interface TicketShowDataProps {
  record?: Ticket;
  subcategory?: any;
  empresa?: any;
  machine?: any;
  reference?: any;
  user?: any;
  substatus?: any;
}

const TicketShowDetail = (props: TicketShowDataProps) => {
  const { t } = useTranslation();
  const { record, subcategory, empresa, machine, reference, user, substatus } = props;
  const fileList = useMemo(() => record?.files?.map(files => new FileData(files)), [record?.files]);
  const descriptionHtml = useMemo(() => ensureHtml(record?.description), [record?.description]);

  return (
    <div className="TicketShowDetail">
      <ShowInfoGrid>
        <ShowInfoItem label={t('Subcategoría')}>
          <TextField value={subcategory?.title_str} />
        </ShowInfoItem>
        <ShowInfoItem label={t('Título')}>
          <TextField value={record?.ticket_title_str} />
        </ShowInfoItem>
        <ShowInfoItem label={t('Empresa')}>
          {record?.empresa ? (
            <LinkTextField
              label={`${empresa?.full_name} (${empresa?.id})`}
              to={`${COMPANY_URLS.ADMIN_EMPRESA}/${empresa?.id}`}
              admin
            />
          ) : (
            '-'
          )}
        </ShowInfoItem>
        <ShowInfoItem label={t('Móvil')}>
          {machine ? (
            <LinkTextField
              label={`${machine?.description} (${machine?.id})`}
              to={`${MACHINES_URLS.ADMIN_MOVIL}/${machine?.id}`}
              admin
            />
          ) : (
            '-'
          )}
        </ShowInfoItem>
        <ShowInfoItem label={t('Asignado a')}>
          <TextField
            value={user ? `${user?.first_name} ${user?.last_name} (${user?.username})` : '-'}
          />
        </ShowInfoItem>
        <ShowInfoItem label={t('Prioridad')}>
          {record?.priority ? <PriorityLabel value={record?.priority} /> : '-'}
        </ShowInfoItem>
        <ShowInfoItem label={t('Subestado')}>
          <TextField value={t(substatus?.title ?? '-')} />
        </ShowInfoItem>
        <ShowInfoItem label={t('Referencia')}>
          {reference ? (
            <LinkTextField
              label={`${reference?.id} - ${reference?.ticket_title_str}`}
              to={'/tickets/show/' + record?.referencia}
              local
            />
          ) : (
            '-'
          )}
        </ShowInfoItem>
        <ShowInfoItem label={t('Tipo de contacto')}>
          {record?.contact_type ? (
            <TextField value={t(TicketContactTypeString[record?.contact_type])} />
          ) : (
            '-'
          )}
        </ShowInfoItem>

        {/* Links adicionales */}
        {record?.movil && (
          <ShowInfoItem label={t('Links adicionales de máquina')} colSpan={2}>
            <div className="TicketShowDataLinks">
              <LinkTextField
                label={t('Info de móvil')}
                to={`/machines/detail/${record?.movil}`}
                platform
              />
              <LinkTextField
                label={t('Ver historial de trabajos')}
                to={`/machines/works/${record?.movil}`}
                platform
              />
              <LinkTextField
                label={t('Ver historial por hora')}
                to={`/machines/history/${record?.movil}`}
                platform
              />
            </div>
          </ShowInfoItem>
        )}

        <ShowInfoItem label={t('Descripción')} colSpan={2}>
          {record?.description ? <FormattedTextInput value={descriptionHtml} readOnly /> : '-'}
        </ShowInfoItem>

        {/* Archivos subidos */}
        {fileList && fileList.length > 0 && (
          <ShowInfoItem label={t('Archivos subidos')} colSpan={2}>
            <UploadedFileCarousel fileList={fileList} />
          </ShowInfoItem>
        )}
      </ShowInfoGrid>
    </div>
  );
};

export default TicketShowDetail;
